<template>
  <v-col
    :class="`report-table pt-0 ${showCurrentSavedFilters ? 'summaries-expanded px-2' : 'px-0'}`"
    @mousemove="keepDragging($event)"
    @mouseup="stopDragging($event)"
  >
    <v-row v-if="isReady">
      <v-col>
        <v-row>
          <v-col class="pb-0">
            <v-card class="elevation-0">
              <v-card-text class="px-2 py-0">
                <v-col>
                  <v-row class="justify-end">
                    <v-col>
                      <v-row>
                        <v-col
                          v-if="showFilterBar"
                          class="col-auto"
                        >
                          <filter-bar />
                        </v-col>

                        <v-col
                          v-if="showFilterBar"
                          class="col-auto"
                        >
                          <v-dialog
                            v-model="columnPickerDialog"
                            max-width="350"
                            :close-on-content-click="false"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                small
                                color="primary"
                                outlined
                                v-bind="attrs"
                                v-on="on"
                              >
                                <font-awesome-icon :icon="['fas', 'sliders']" />
                                <span class="pl-1">Columns</span>
                              </v-btn>
                            </template>

                            <column-picker
                              :current-required-columns="currentRequiredColumns"
                              :report-key="reportKey"
                              :table-columns="tableColumns"
                            />
                          </v-dialog>
                        </v-col>

                        <v-col
                          v-if="showItemBar"
                          :class="showItemBar ? 'col-auto text-xs-center' : 'd-none'"
                        >
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click.stop="createItem(undefined)"
                          >
                            <font-awesome-icon :icon="['fas', 'plus-square']" />
                            <span class="pl-1">Add Item</span>
                          </v-btn>
                        </v-col>

                        <v-spacer />

                        <v-col
                          v-if="showExportBar"
                          class="col-auto text-center"
                        >
                          <v-menu
                            :disabled="!showExportBar"
                            min-width="90"
                            offset-y
                            nudge-bottom="6"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="primary"
                                small
                                v-bind="attrs"
                                v-on="on"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'circle-down']"
                                  size="sm"
                                />

                                <span class="pl-1">Export</span>
                              </v-btn>
                            </template>

                            <v-list>
                              <v-list-item
                                v-for="item in exportOptions"
                                :key="item.key"
                                :disabled="!item.active"
                                @click="item.callback"
                              >
                                <v-list-item-title>
                                  <v-col>
                                    <v-row v-if="item.key !== 'xlsx'">
                                      {{ item.text }}
                                    </v-row>

                                    <v-row v-else>
                                      <export-xlsx
                                        :item="item"
                                        :show-export-bar="showExportBar"
                                      />
                                    </v-row>
                                  </v-col>
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row
          v-for="group of tableGroups"
          :key="group.groupName"
          :class="`table-group rounded-borders`"
        >
          <v-col
            v-if="!group.isPartOfGroup || group.items.length > 0"
            :class="`${group.isPartOfGroup ? 'rounded-borders cursor-pointer px-6' : `px-0 pt-0 ${inDarkMode ? 'vuetifyblack' : 'icewhite'}`}`"
          >
            <v-row
              class="row--dense overline mb-1"
              @click="toggleRows(group.groupName)"
            >
              <v-col
                v-if="group.groupName"
                class="d-inline-flex flex-grow col-10 align-center py-4"
              >
                <v-row>
                  <span class="font-weight-bold primary--text">
                    {{ group.labels[0] }}
                  </span>

                  <span
                    class="font-weight-regular pl-3"
                  >
                    {{ group.labels.slice(1).join(' :: ') }}
                  </span>
                </v-row>
              </v-col>

              <v-spacer v-if="!isProgrammaticRoute" />

              <v-col
                v-if="group.groupName && !isProgrammaticRoute"
                :class="`d-inline-flex align-center justify-end coolgray--text text-right caption col-1 ${group.isPartOfGroup ? '' : 'pt-0'}`"
              >
                <div class="font-weight-medium">
                  {{ formatNumber(group.items.length) }}
                </div>

                <div class="d-inline-flex d-sm-none d-md-inline d-lg-inline d-xl-inline align-center font-weight-light pl-1">
                  {{ group.items.length === 1 ? 'Result' : 'Results' }}
                </div>
              </v-col>

              <v-col
                v-if="isProgrammaticRoute"
                :class="`align-center justify-end coolgray--text caption ${group.isPartOfGroup ? '' : 'pt-0'}`"
              >
                <v-row class="align-center px-4">
                  <v-col
                    v-for="(summary, summaryIndex) in group.summaries"
                    :key="summaryIndex"
                    :class="`col-auto coolgray--text justify-start my-0`"
                    :title="summary.title"
                    :style="`min-width: ${summary.widthPx};`"
                  >
                    <span class="font-weight-bold">
                      {{ summary.value }}
                    </span>

                    <span class="align-center font-weight-regular pl-1">
                      {{ summary.title }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-data-table
              v-if="!group.isPartOfGroup || showRows.indexOf(group.groupName) !== -1"
              cell-class="font-weight-regular"
              dense
              :custom-sort="setTableSorting"
              :footer-props="tableFooterProps"
              :headers="visibleColumns"
              hide-default-header
              hide-default-footer
              :items="group.items || []"
              :items-per-page="syncItemsPerPage"
              :loading="group.items.length > 0 && (applyingFilter || resettingFilter)"
              multi-sort
              :page.sync="syncPage"
              :sort-by.sync="sortedByKeys"
              :sort-desc.sync="sortedByDirections"
              :class="`cursor-context-menu scroller tbody ${group.isPartOfGroup ? 'is-part-of-group' : ''}`"
              @page-count="syncPageCount = $event"
              @scroll="calculatePixelsFromTableTop"
            >
              <template v-slot:header="{ props: { headers } }">
                <thead
                  ref="tableHeaderRow"
                  :style="`position: sticky; top: ${pixelsFromTableTop};`"
                  :class="inDarkMode ? 'black' : 'white'"
                  @contextmenu="toggleColumnMenu($event)"
                  @dblclick="toggleColumnMenu($event)"
                >
                  <tr>
                    <th
                      v-for="(header, headerIndex) in headers"
                      :key="headerIndex"
                      :class="`coolgray--text font-weight-semibold ${header.classes}`"
                      :style="header.styles"
                      :title="header.tooltip || header.text"
                    >
                      <v-row>
                        <v-col
                          :class="`col-10 d-flex align-center justify-${header.align} py-0`"
                          :style="`width: ${header.widthPx};`"
                        >
                          <div
                            :class="`text-truncate ${header.sortClassText}`"
                          >
                            {{ header.text }}
                          </div>
                          <v-chip
                            :class="`cursor-pointer ${inDarkMode ? 'black' : 'white'} ${header.sortClassIndicator}`"
                            label
                            outlined
                            overlap
                            top
                            x-small
                            @click="updateSorting(header)"
                          >
                            <v-row>
                              <v-icon
                                small
                              >
                                {{ header.sortIcon }}
                              </v-icon>
                              <div class="text-smaller">
                                {{ (header.sortIndex !== -1 && header.sortIndex) || '–' }}
                              </div>
                            </v-row>
                          </v-chip>
                        </v-col>

                        <column-resizer
                          :column-index="headerIndex"
                          :column-width="header.width"
                        />
                      </v-row>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:item="{ headers, item, index }">
                <tr
                  :class="(contextMenuItemIndex && contextMenuItemIndex === index && (inDarkMode ? 'black' : 'coolbeige')) || (inDarkMode ? 'black' : 'white')"
                  @contextmenu="toggleContextMenu($event, item, index)"
                  @dblclick="toggleContextMenu($event, item, index)"
                  @click="resetContextMenu(null)"
                >
                  <td
                    v-for="(header, hIndex) in headers"
                    :key="hIndex"
                    :class="header.classes"
                    :style="header.styles"
                  >
                    <v-row
                      :style="`width: ${header.widthPx};`"
                    >
                      <table-column
                        :header="header"
                        :item="item"
                      />
                    </v-row>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <v-menu
              v-model="contextMenu"
              absolute
              offset-y
              :position-x="contextMenuX"
              :position-y="contextMenuY"
              transition="scale-transition"
              width="300px"
            >
              <v-card>
                <v-card-actions>
                  <v-list dense>
                    <v-list-item
                      v-for="(action, actionIndex) in actions"
                      :key="actionIndex"
                      @click="fireActionEvent(action, contextMenuItem)"
                    >
                      <v-list-item-content class="body-2">
                        {{ action.name }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-actions>
              </v-card>
            </v-menu>

            <!-- Right-click column menu for power users -->
            <v-menu
              v-model="columnMenu"
              absolute
              max-height="300"
              min-width="300"
              offset-y
              :close-on-content-click="false"
              :position-x="columnMenuX"
              :position-y="columnMenuY"
              bottom
              left
              transition="scale-transition"
              width="300px"
            >
              <column-picker
                :current-required-columns="currentRequiredColumns"
                :report-key="reportKey"
                :table-columns="tableColumns"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col class="pt-0">
            <v-card class="elevation-0">
              <v-card-text class="px-2 py-0">
                <v-col>
                  <v-row>
                    <v-col>
                      <v-row class="justify-end">
                        <v-col
                          v-if="showFilterBar"
                          class="col-auto text-center"
                        >
                          <v-row>
                            <v-col
                              class="col-auto px-0 text-center"
                              style="max-width: 120px;"
                            >
                              <!-- Shift Page Left -->
                              <v-btn
                                class="caption font-weight-medium"
                                :color="syncPage === 1 ? 'grey' : 'primary'"
                                icon
                                small
                                :disabled="syncPage === 1"
                                @click="shiftPageLeft"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'angle-left']"
                                  size="sm"
                                />
                              </v-btn>
                            </v-col>

                            <v-col
                              class="col-auto text-truncate"
                              style="width: fit-content"
                            >
                              <span
                                class="caption font-weight-medium coolgray--text"
                                style="line-height: 30px;"
                              >
                                {{ rowsPerPageLabel }}
                              </span>
                            </v-col>

                            <v-col
                              class="col-auto px-0 text-center"
                            >
                              <!-- Shift Page Right -->
                              <v-btn
                                class="caption font-weight-medium"
                                :color="syncPage === syncPageCount ? 'grey' : 'primary'"
                                :disabled="syncPage === syncPageCount"
                                icon
                                small
                                @click="shiftPageRight"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'angle-right']"
                                  size="sm"
                                />
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-col>

                        <v-col
                          v-if="showFilterBar"
                          class="col-auto font-weight-medium"
                        >
                          <v-row>
                            <v-col>
                              <v-menu
                                v-model="showRowsPerPageMenu"
                                offset-y
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    v-bind="attrs"
                                    color="primary"
                                    small
                                    outlined
                                    class="caption font-weight-medium"
                                    v-on="on"
                                  >
                                    <span>{{ itemsPerPage === -1 ? 'All Rows' : `${formatNumber(itemsPerPage)} Rows / Page` }}</span>

                                    <font-awesome-icon
                                      :icon="['fas', 'chevron-down']"
                                      class="ml-2"
                                    />
                                  </v-btn>
                                </template>

                                <v-list>
                                  <v-list-item
                                    v-for="option in itemsPerPageOptions"
                                    :key="option.value"
                                    @click="syncItemsPerPage = option.value"
                                  >
                                    <v-list-item-title>
                                      {{ option.text }}
                                    </v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import _ from 'underscore'
import { createNamespacedHelpers } from 'vuex'

import { fireanalytics } from 'src/store/firebase'

import dates from 'src/utils/dates'
import format from 'src/utils/format'
import sort from 'src/utils/sort'

import ColumnPicker from './ColumnPicker'
import ColumnResizer from './ColumnResizer'
import TableColumn from './TableColumn'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutsGetters, mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapActions, mapGetters, mapState } = createNamespacedHelpers('tools')

const { direction: SortDirection, label: SortLabel } = sort

export default {
  name: 'DataTable',
  components: {
    // Dynamic import caused a split-second lag.
    ColumnPicker,
    ColumnResizer,
    TableColumn,

    ExportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    FilterBar: () => import('src/tools/components/filter/FilterBar'),
  },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    dataGroups: {
      type: Array,
      default: null,
    },
    initialItemsPerPage: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      columnMenu: false,
      columnMenuX: 0,
      columnMenuY: 0,
      columnPickerDialog: false,
      contextMenu: false,
      contextMenuItem: null,
      contextMenuItemIndex: null,
      contextMenuX: 0,
      contextMenuY: 0,
      format,
      observer: null,
      showRowsPerPageMenu: false,
      SortDirection,
      tableOptions: {},
    }
  },
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
      'isInternalWriter',
    ]),

    ...mapGetters([
      'columnsByKey',
      'currentColumns',
      'currentGroupByProperties',
      'currentGroups',
      'currentlySorting',
      'currentNumberOfAppliedFilters',
      'currentSorted',
      'currentSorting',
      'currentSummary',
      'currentViewId',
      'frozenColumns',
      'isProgrammaticRoute',
      'keyField',
      'reportColumns',
      'reportKey',
      'showCurrentSavedFilters',
      'sizeField',
      'sortByDirections',
      'sortByKeys',
      'storedItems',
      'tableColumns',
      'visibleColumns',
    ]),
    ...mapState({
      applyingFilter: (state) => state.applyingFilter,
      displaySavedFilters: (state) => state.displaySavedFilters,
      dragging: (state) => state.dragging,
      hasClickedToggler: (state) => state.hasClickedToggler,
      internalRoutes: (state) => state.internalRoutes,
      isTogglingTables: (state) => state.isTogglingTables,
      itemsPerPage: (state) => state.itemsPerPage,
      itemsPerPageOptions: (state) => state.itemsPerPageOptions,
      page: (state) => state.page,
      pageCount: (state) => state.pageCount,
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      resettingFilter: (state) => state.resettingFilter,
      showRows: (state) => state.showRows,
      subId: (state) => state.subId,

      requiredColumns: (state) => state.requiredColumns,
      sortClassIndicatorMap: (state) => state.sortClassIndicatorMap,
      sortClassTextMap: (state) => state.sortClassTextMap,
      sortIcons: (state) => state.sortIcons,
      sortingByKeys: (state) => state.sortingByKeys,
      sortingByDirections: (state) => state.sortingByDirections,
      tableSortMap: (state) => state.tableSortMap,
    }),

    ...mapLayoutsGetters([
      'showDataTools',
      'showExportBar',
      'showFilterBar',
      'showLayoutBar',
    ]),

    ...mapLayoutState({
      icons: (state) => state.icons,
    }),

    pixelsFromTableTop() {
      // Get the number of pixels from the top of the first row to the top of the first visible row.
      const { tableHeaderRow } = this.$refs
      const tableHeaderRowTop = tableHeaderRow?.getBoundingClientRect().top
      const tableHeaderRowHeight = tableHeaderRow?.clientHeight

      return tableHeaderRowTop + tableHeaderRowHeight
    },

    syncPage: {
      get() {
        return this.page
      },
      set(value) {
        this.$store.commit('tools/page', value)
      },
    },
    syncPageCount: {
      get() {
        return this.pageCount
      },
      set(value) {
        this.$store.commit('tools/pageCount', value)
      },
    },
    syncItemsPerPage: {
      get() {
        // Use prop value if provided, otherwise fall back to store value
        return this.initialItemsPerPage !== null ? this.initialItemsPerPage : this.itemsPerPage
      },
      set(value) {
        this.$store.commit('tools/itemsPerPage', value)
      },
    },

    currentRowsIndexLast() {
      const indexLastPossible = this.currentRowCount && (this.syncItemsPerPage * this.syncPage)
      return Math.min(indexLastPossible, this.currentRowCount)
    },
    currentRowsIndexFirst() {
      if (!this.currentRowCount) return 0
      return (this.syncPage - 1) * this.syncItemsPerPage + 1
    },
    rowsPerPageLabel() {
      const currentRowCount = this.currentRowCount && format.number(this.currentRowCount)
      const isAllRows = this.syncItemsPerPage === -1
      const lastRowOnPage = isAllRows ? currentRowCount : this.currentRowsIndexLast
      const firstRowOnPage = this.currentRowsIndexFirst

      return `Rows ${firstRowOnPage} - ${lastRowOnPage} of ${currentRowCount}`
    },
    showItemBar() {
      const { addIcon, internalRoutes, isInternalWriter, reportKey } = this
      const isAddRoute = () => addIcon && internalRoutes.includes(reportKey)

      return reportKey && isInternalWriter && isAddRoute()
    },

    sortedByDirections: {
      get() {
        return this.sortByDirections
      },
      set(newValue) {
        this.$store.commit('tools/sortingByDirections', newValue)
      },
    },
    sortedByKeys: {
      get() {
        return this.sortByKeys
      },
      set(newValue) {
        this.$store.commit('tools/sortingByKeys', newValue)
      },
    },

    currentRequiredColumns() {
      return this.requiredColumns[this.reportKey] || []
    },
    addPaddingToTable() {
      const { displaySavedFilters, reportKey } = this
      const isPacingReport = reportKey === 'pacing'

      return !isPacingReport || displaySavedFilters[reportKey]
    },
    isReady() {
      return !!this.currentViewId
    },
    isReadyAndHasResults() {
      const { tableGroups, isReady } = this
      return isReady && !!tableGroups?.length
    },
    isReadyAndNoData() {
      const { tableGroups, isReady } = this

      return isReady && !tableGroups?.length
    },
    allGroupsAreEmpty() {
      const { tableGroups } = this
      const foundItems = tableGroups.find((x) => x.items.length > 0)

      return !foundItems
    },
    tableGroups() {
      return this.dataGroups || this.currentGroups
    },
    // Override currentRowCount when dataGroups is provided
    currentRowCount() {
      if (this.dataGroups) {
        // Calculate from tableGroups when custom dataGroups is provided
        return this.tableGroups.reduce((rowCount, currentGroup) => {
          rowCount += currentGroup.items.length
          return rowCount
        }, 0)
      }
      // Fall back to store's currentRowCount when no custom dataGroups
      return this.$store.getters['tools/currentRowCount']
    },
    tableFooterProps() {
      return {
        absolute: true,
        'items-per-page-options': this.itemsPerPageOptions,
      }
    },
    topPadding() {
      return this.currentGroupByProperties.length ? 'mt-1' : 'mt-0'
    },
    addIcon() {
      const { icons: { navigation: { add: addIcons } }, reportKey } = this

      return (!!reportKey && addIcons[reportKey]) || null
    },
    layouts() {
      const { tableGroups, showFilterBar, showLayoutBar } = this
      const isToggleGroupsActive = showFilterBar && !!tableGroups?.length && tableGroups[0].groupName !== null

      return [
        {
          active: isToggleGroupsActive,
          helpText: 'See Group & Filter to activate',
          key: 'groups',
          text: 'Toggle Groups',
          callback: this.toggleGroups,
        },
        {
          active: showLayoutBar,
          helpText: 'No saved filters to toggle',
          key: 'summaries',
          text: 'Toggle Saved Filters',
          callback: this.toggleSummaries,
        },
      ]
    },

    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },
  },
  created() {
    // Initialize store with prop value if provided
    if (this.initialItemsPerPage !== null) {
      this.$store.commit('tools/itemsPerPage', this.initialItemsPerPage)
    }
  },
  methods: {
    ...mapActions(['keepDragging', 'startDragging', 'stopDragging', 'toggleRows']),
    toggleColumnMenu(event) {
      event.preventDefault()

      const newValue = !this.columnMenu

      if (newValue) {
        this.columnMenuX = event.clientX
        this.columnMenuY = event.clientY
        this.columnMenu = newValue
      } else {
        this.columnMenu = newValue
      }
    },
    resetContextMenu(newValue) {
      this.contextMenu = newValue
      this.contextMenuItemIndex = null
      this.contextMenuItem = null
    },
    toggleContextMenu(event, item, itemIndex) {
      event.preventDefault()

      const newValue = !this.contextMenu

      if (newValue) {
        this.contextMenuItem = item
        this.contextMenuItemIndex = itemIndex
        this.contextMenuX = event.clientX
        this.contextMenuY = event.clientY
        this.contextMenu = newValue
      } else {
        this.resetContextMenu(newValue)
      }
    },
    fireActionEvent(action, item) {
      this.$store.dispatch(action.event, item)
    },
    haveDataButNoResultsInGroup(group) {
      const { allGroupsAreEmpty, isReadyAndHasResults } = this

      if (allGroupsAreEmpty) return false

      return isReadyAndHasResults && group.items.length === 0
    },
    getClassNames(value, column) {
      const { formatAs = 'text' } = column
      const isNonTextType = ['boolean', 'number', 'percentage'].indexOf(formatAs) !== -1
      const formatClass = `column-${isNonTextType ? 'narrow' : formatAs}`
      const xAxisPadding = 'pl-0 pr-3'
      // const isFirstColumn = columnIndex === 0
      // const xAxisPadding = isFirstColumn ? 'px-3' : 'pl-0 pr-3'

      return `${xAxisPadding} ${formatClass} ${column.classes}`
    },
    formatNumber(value) {
      return format.number(value)
    },
    sortItems({ columnKey, columnDirection, columnFormat, items }) {
      const sortDsc = columnDirection === this.tableSortMap.DSC
      const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
      const isDateColumn = columnFormat === 'date'

      const valueGetter = (item) => {
        const getValue = () => {
          const value = !isDateColumn && item.normalized ? item.normalized[columnKey] : item[columnKey]
          const sortArrayValues = () => (sortDsc ? value.sort().reverse() : value.sort())
          return Array.isArray(value) ? sortArrayValues()[0] : value?.value || value
        }
        const getDate = (value) => dates.getUnixDate(value)
        return isDateColumn ? getDate(getValue()) : getValue()
      }

      return sorter(items, valueGetter)
    },
    addSortingOption(optionIndex, header) {
      const { ASC } = this.tableSortMap
      const addOption = () => {
        this.sortedByKeys.splice(this.sortedByKeys.length, 0, header.key)
        this.sortedByDirections.splice(this.sortedByDirections.length, 0, ASC)

        header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = this.sortedByKeys.length

        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Add Sort Option',
          label: `Add Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        return true
      }

      return (optionIndex === -1 && addOption()) || false
    },
    deleteSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const removeOption = () => {
        fireanalytics.event('goal_completion', {
          action: 'click',
          category: 'Sort',
          description: 'Remove Sort Option',
          label: `Remove Sort Option "${header.key}" with Direction "${SortLabel[header.sortDsc]}" at Index ${header.sortIndex}`,
        })

        this.sortedByDirections.splice(optionIndex, 1)
        this.sortedByKeys.splice(optionIndex, 1)

        this.sortedByKeys.forEach((key, sortIndex) => {
          const foundHeader = this.columnsByKey[key]
          foundHeader.sortIndex = sortIndex + 1
        })

        // @TODO Avoid issue w/ sortClassIndicator's text color pre- vs post-click by abstracting the values.
        header.sortClassIndicator = `midgray--text ${this.sortClassIndicatorMap[header.align]}`
        header.sortDsc = ASC
        header.sortIcon = this.sortIcons.ASC
        header.sortIndex = -1

        return true
      }
      return (this.sortedByDirections[optionIndex] === DSC && removeOption()) || false
    },
    updateSortingOption(optionIndex, header) {
      const { ASC, DSC } = this.tableSortMap
      const currentDirection = this.sortedByDirections[optionIndex]
      const newDirection = currentDirection === DSC ? ASC : DSC
      const newSortIndex = optionIndex + 1

      fireanalytics.event('goal_completion', {
        action: 'click',
        category: 'Sort',
        description: 'Update Sort Option',
        label: `Update Sort Option "${header.columnName}" (${header.key}) from Direction "${SortLabel[header.sortDsc]}" to Direction "${newDirection}" at Index ${header.sortIndex}`,
      })

      header.sortClassIndicator = `brightblue--text ${this.sortClassIndicatorMap[header.align]}`
      header.sortDsc = newDirection
      header.sortIcon = this.sortIcons[newDirection]
      header.sortIndex = newSortIndex

      this.sortedByDirections.splice(optionIndex, 1, newDirection)

      return true
    },
    updateSorting(header) {
      const optionIndex = this.sortedByKeys.findIndex((key) => key === header.key)

      return this.addSortingOption(optionIndex, header)
          || this.deleteSortingOption(optionIndex, header)
          || this.updateSortingOption(optionIndex, header)
    },
    setTableSorting(unsortedItems) {
      const { columnsByKey } = this
      const sortItems = (items, columnKey, index) => this.sortItems({
        columnDirection: this.sortedByDirections[index],
        columnFormat: columnsByKey[columnKey]?.formatAs,
        columnKey,
        items,
      })

      return this.sortedByKeys.reduceRight(sortItems, unsortedItems)
    },

    shiftPageLeft() {
      // this.syncPage = Math.max(this.syncPage - 1, 1)
      if (this.syncPage > 1) {
        this.syncPage -= 1
      }
    },
    shiftPageRight() {
      // this.syncPage = Math.min(this.syncPage + 1, this.syncPageCount)
      if (this.syncPage < this.syncPageCount) {
        this.syncPage += 1
      }
    },

    calculatePixelsFromTableTop() {
      const { tableHeaderRow } = this.$refs
      const tableHeaderRowTop = tableHeaderRow?.getBoundingClientRect().top
      const tableHeaderRowHeight = tableHeaderRow?.clientHeight

      this.pixelsFromTableTop = tableHeaderRowTop + tableHeaderRowHeight
    },

    toggleSummaries() {
      this.$store.dispatch('layout/toggleSummaries')
    },

    createItem(value) {
      this.$store.dispatch('internal/createItem', value)
    },

    toggleGroups() {
      this.$store.dispatch('tools/toggleGroups')
    },

    exportAsCsv() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },
    exportAsXlsx() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

  },
}
</script>

<style lang="scss" scoped>
@import "src/styles/tables";

#header-thead {
  position: sticky;
  top: 0;
}

#header-tbody {
  position: absolute;
  top: 0;

  overflow: scroll;
  width: max-content;

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
#header-tbody::-webkit-scrollbar {
  display: none;
}
</style>
